import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Link2, SlidersHorizontal, BadgeCheck, Eye, ShieldCheck, FileSearch, Handshake } from "lucide-react";
import Seo, { organizationJsonLd, websiteJsonLd } from "@/components/Seo";
import { Container, Section, Eyebrow } from "@/components/ui-kit/Section";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import MaskText from "@/components/motion/MaskText";
import Marquee from "@/components/motion/Marquee";
import DepositSplitMock from "@/components/DepositSplitMock";
import IncomeCalculator from "@/components/calculators/IncomeCalculator";
import DualAudience from "@/components/blocks/DualAudience";
import FinalCta from "@/components/blocks/FinalCta";
import Cta from "@/components/ui-kit/Cta";

const STEPS = [
  {
    icon: Link2,
    title: "Link your bank",
    body: "Connect read only through Plaid. ETAP reads transactions to detect income. It never sees or stores your bank login.",
  },
  {
    icon: SlidersHorizontal,
    title: "Set your percentage",
    body: "Choose the federal and state percentages you want to route. You decide the number, and you can change it anytime.",
  },
  {
    icon: BadgeCheck,
    title: "Get paid and stay current",
    body: "When income arrives, ETAP routes your set aside toward taxes and shows a live view of what moved and what stays.",
  },
];

const TRUST = [
  { icon: Eye, title: "Read only bank access", body: "Through Plaid. ETAP never stores your bank credentials." },
  { icon: ShieldCheck, title: "ETAP never holds your funds", body: "Money moves from your account to tax authorities." },
  { icon: FileSearch, title: "Every transfer auditable", body: "Each split, fee, and transfer is recorded on your dashboard." },
  { icon: Handshake, title: "Consent required, revocable", body: "Nothing moves without your consent. Withdraw it anytime." },
];

export default function Home() {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const mockY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -48]);

  return (
    <>
      <Seo
        title="ETAP | Make estimated taxes invisible"
        description="ETAP routes your set aside for federal and state estimated taxes the moment income lands. Read only bank access, non custodial, 0.5 percent flat. Start free."
        path="/"
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden pt-28 pb-20 sm:pt-32 lg:pt-40 lg:pb-28">
        {/* Video background with dark scrim for legibility */}
        <div className="absolute inset-0 -z-10 bg-etap-deep-900">
          {reduce ? (
            <img src="/media/hero-poster.jpg" alt="" aria-hidden="true" className="h-full w-full object-cover" />
          ) : (
            <video
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              poster="/media/hero-poster.jpg"
              aria-hidden="true"
              tabIndex={-1}
            >
              <source src="/media/hero.mp4" type="video/mp4" />
            </video>
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(105deg, rgba(9,20,31,0.97) 0%, rgba(13,28,42,0.92) 45%, rgba(17,34,50,0.84) 100%)",
            }}
          />
        </div>

        <Container>
          <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-6">
              <Reveal>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-etap-mint-100 backdrop-blur">
                  <span className="h-1.5 w-1.5 rounded-full bg-etap-mint-400" /> Estimated Tax Automated Payments
                </span>
              </Reveal>
              <h1 className="mt-6 font-display text-[clamp(2.5rem,5.4vw,4.5rem)] font-extrabold leading-[1.02] tracking-tight text-white">
                <MaskText
                  lines={["Your taxes,", "set aside the moment", "you get paid."]}
                  accentIndex={1}
                  accentClassName="text-etap-mint-200"
                  delay={0.15}
                />
              </h1>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/80">
                  Income is detected, then the percentage you choose is routed to federal and state taxes,
                  automatically. ETAP never holds your money. It moves from your linked account to tax authorities.
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Cta to="/get-started" variant="mint" size="lg" location="hero" withArrow testId="hero-primary-cta">
                    Start Free Setup
                  </Cta>
                  <Cta to="#how-it-works" variant="onDeepOutline" size="lg" location="hero-how" testId="hero-secondary-cta">
                    See How It Works
                  </Cta>
                </div>
              </Reveal>
              <Reveal delay={0.4}>
                <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-white/70">
                  <li className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-etap-mint-300" aria-hidden="true" /> Read only via Plaid
                  </li>
                  <li className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-etap-mint-300" aria-hidden="true" /> Non custodial
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSearch className="h-4 w-4 text-etap-mint-300" aria-hidden="true" /> Authorized via Stripe
                  </li>
                </ul>
              </Reveal>
            </div>

            <div className="flex justify-center lg:col-span-6 lg:justify-end">
              <motion.div style={{ y: mockY }} className="w-full max-w-md">
                <DepositSplitMock />
              </motion.div>
            </div>
          </div>
        </Container>
      </section>

      {/* Editorial marquee */}
      <div className="border-b border-etap-deep-100 bg-white py-4">
        <Marquee
          items={["Freelancers", "Designers", "Consultants", "Gig workers", "Contractors", "Accountants", "Agencies"]}
        />
      </div>

      {/* HOW IT WORKS */}
      <Section id="how-it-works" className="bg-canvas">
        <Container>
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>How it works</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                Three steps, then it runs quietly in the background
              </h2>
              <p className="mt-4 text-lg text-etap-deep-600">
                Setup takes a few minutes. After that, every deposit is handled the way you decided, and you keep a clear
                record of where each dollar went.
              </p>
            </Reveal>
          </div>

          <RevealGroup className="mt-14 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <RevealItem
                key={s.title}
                className="relative rounded-3xl border border-etap-deep-100 bg-white p-8 shadow-etap-sm transition-shadow duration-300 hover:shadow-etap-lg"
                data-testid={`home-step-${i}`}
              >
                <span className="absolute right-6 top-6 font-display text-5xl font-extrabold text-etap-primary-100">
                  0{i + 1}
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-etap-deep text-white">
                  <s.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-etap-deep-800">{s.title}</h3>
                <p className="mt-3 text-etap-deep-600">{s.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>
        </Container>
      </Section>

      {/* TRUST BAND */}
      <section className="relative overflow-hidden bg-etap-mint-100/70 py-20 sm:py-24">
        <Container>
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>Trust and transparency</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                Built for people who want to see every dollar move
              </h2>
              <p className="mt-4 text-lg text-etap-deep-700">
                ETAP is not a bank and not a custodian. It reads what it needs to detect income, then routes your set
                aside on your instruction. You stay in control at every step.
              </p>
            </Reveal>
          </div>

          <RevealGroup className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST.map((t) => (
              <RevealItem
                key={t.title}
                className="rounded-2xl border border-etap-mint-300 bg-white p-6 shadow-etap-sm"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-etap-mint-200 text-etap-deep-700">
                  <t.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-etap-deep-800">{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-etap-deep-600">{t.body}</p>
              </RevealItem>
            ))}
          </RevealGroup>

          {/* Placeholder proof strip, clearly labeled for client-provided logos */}
          <Reveal className="mt-14">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-etap-deep-500">
              Trusted by (logos to be provided)
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
              {[1, 2, 3, 4, 5].map((n) => (
                <div
                  key={n}
                  className="flex h-12 w-32 items-center justify-center rounded-xl border border-dashed border-etap-deep-200 bg-white/70 text-xs font-medium text-etap-deep-400"
                  data-testid={`logo-placeholder-${n}`}
                >
                  Logo {n}
                </div>
              ))}
            </div>
          </Reveal>
        </Container>
      </section>

      {/* DUAL AUDIENCE */}
      <Section className="bg-canvas">
        <Container>
          <div className="mb-12 max-w-2xl">
            <Reveal>
              <Eyebrow>Two ways to use ETAP</Eyebrow>
              <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                For the person getting paid, and the firm managing many
              </h2>
            </Reveal>
          </div>
          <DualAudience location="home-dual" />
        </Container>
      </Section>

      {/* PRICING TEASER */}
      <Section className="bg-white">
        <Container>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>Pricing</Eyebrow>
                <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
                  One flat fee. 0.5% per transaction. Nothing hidden.
                </h2>
                <p className="mt-4 text-lg text-etap-deep-600">
                  Drag the sliders to see your set aside, the ETAP fee, and what stays in your account. The fee is a hard
                  ceiling in code, so you never pay more than 0.5 percent of a routed amount.
                </p>
                <div className="mt-7">
                  <Cta to="/pricing" variant="primary" location="home-pricing" withArrow testId="home-pricing-cta">
                    See Full Pricing
                  </Cta>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <IncomeCalculator variant="mini" />
              </Reveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* CLOSING NARRATIVE for indexable depth */}
      <Section className="bg-canvas pt-0">
        <Container>
          <Reveal className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-[clamp(1.75rem,3.4vw,2.5rem)] font-bold leading-tight tracking-tight text-etap-deep-900">
              Estimated taxes should not require a spreadsheet
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-etap-deep-600">
              When you work for yourself, the tax you owe does not arrive with a paycheck stub. It is on you to guess a
              number, remember four deadlines a year, and hope you set enough aside. ETAP replaces that guesswork with a
              simple rule you control. Money comes in, your chosen percentage is routed toward federal and state taxes,
              and the rest stays yours. You always see the split, the fee, and the running total, so there is nothing to
              reconcile at quarter end.
            </p>
            <div className="mt-8 inline-flex flex-wrap justify-center gap-3">
              <Cta to="/how-it-works" variant="outline" location="home-narrative" withArrow testId="home-narrative-cta">
                Walk through the full flow
              </Cta>
            </div>
          </Reveal>
        </Container>
      </Section>

      <FinalCta location="home-final" />
    </>
  );
}
